import { type DragEvent, useState } from "react";
import {
  GALLERY_SESSION_DRAG_TYPE,
  GALLERY_SESSION_REQUIRES_ORG_DRAG_TYPE,
} from "./galleryTree";

/**
 * Wires drag-over / drop handlers for an element that should accept a session
 * being dragged into the given project. Returns ``isOver`` for the allowed
 * highlight, ``isForbidden`` for the rejected highlight, plus the three
 * dataTransfer-aware handlers as ``dropProps``.
 *
 * Pass ``null`` for ``projectId`` to mean "personal root" — the drop will
 * fire ``onMove(sessionId, null)``, which the backend treats as moving the
 * session out of any folder. Pass ``null`` for ``onMove`` to disable the
 * target entirely.
 *
 * Set ``isOrgShared`` when the target is part of the active org's tree
 * (org root or any of its descendants). When the dragged session is being
 * accessed via org-share rather than ownership, only org-shared targets
 * accept the drop — personal targets get a ``no-drop`` cursor and skip the
 * highlight, so the user sees up-front that the move would be rejected.
 */
export function useSessionDropTarget<T extends HTMLElement>(
  projectId: string | null,
  onMove:
    | ((sessionId: string, projectId: string | null) => void | Promise<void>)
    | null
    | undefined,
  options: { isOrgShared?: boolean } = {},
) {
  const [isOver, setIsOver] = useState(false);
  const [isForbidden, setIsForbidden] = useState(false);
  const enabled = Boolean(onMove);
  const isOrgShared = options.isOrgShared ?? false;

  const onDragOver = (event: DragEvent<T>) => {
    if (
      !(enabled && event.dataTransfer.types.includes(GALLERY_SESSION_DRAG_TYPE))
    ) {
      return;
    }
    // Stop bubbling so a parent drop target (e.g., a section wrapper) doesn't
    // also light up while we're already hovering a more specific child.
    event.stopPropagation();
    const requiresOrg = event.dataTransfer.types.includes(
      GALLERY_SESSION_REQUIRES_ORG_DRAG_TYPE,
    );
    if (requiresOrg && !isOrgShared) {
      // Don't preventDefault → browser shows the no-drop cursor and the
      // drop event won't fire for this target.
      event.dataTransfer.dropEffect = "none";
      setIsForbidden(true);
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setIsOver(true);
  };

  const onDragLeave = () => {
    setIsOver(false);
    setIsForbidden(false);
  };

  const onDrop = async (event: DragEvent<T>) => {
    if (!(enabled && onMove)) return;
    // Same reason as onDragOver — keep the drop scoped to the most specific
    // target, so wrapping a section in a drop zone doesn't double-fire when
    // a child target already handled the drop.
    event.stopPropagation();
    event.preventDefault();
    setIsOver(false);
    setIsForbidden(false);
    const sessionId = event.dataTransfer.getData(GALLERY_SESSION_DRAG_TYPE);
    if (!sessionId) return;
    await onMove(sessionId, projectId);
  };

  return {
    isOver,
    isForbidden,
    dropProps: { onDragOver, onDragLeave, onDrop },
  };
}
